/**
 * 案件サービス（実API実装）
 *
 * バックエンドAPI呼び出し:
 * - GET /api/projects - 案件一覧取得
 * - POST /api/projects - 新規案件作成
 * - GET /api/projects/:id - 案件詳細取得
 * - PUT /api/projects/:id - 案件更新
 * - DELETE /api/projects/:id - 案件削除
 * - GET /api/projects/:id/checks - チェック結果取得
 * - PUT /api/projects/:id/checks - チェック結果更新
 * - GET /api/projects/:id/access - アクセス権取得
 * - POST /api/projects/:id/access - アクセス権追加
 */
import apiClient, { extractErrorMessage } from '../../lib/apiClient';
import type {
  Project,
  ProjectDetail,
  MAMethod,
  ProjectStatus,
  Country,
  CheckResult,
  CheckStatus,
  ProjectAccess,
  User,
  RiskCategory,
} from '../../types';

// フィルター型
export interface ProjectFilters {
  keyword?: string;
  maMethod?: MAMethod | '';
  status?: ProjectStatus | '';
  targetCountry?: Country | '';
}

export interface AdminProjectFilters extends ProjectFilters {
  creatorId?: string | '';
}

// 案件統計
export interface ProjectStats {
  total: number;
  byStatus: Record<ProjectStatus, number>;
  byMethod: Record<MAMethod, number>;
}

// 案件と作成者情報
export interface ProjectWithCreator extends Project {
  creator?: User;
}

// リクエスト型
export interface CreateProjectRequest {
  name: string;
  maMethod: MAMethod;
  targetCompany: string;
  targetCountry: Country;
  description?: string;
}

export interface UpdateProjectRequest {
  name?: string;
  maMethod?: MAMethod;
  targetCompany?: string;
  targetCountry?: Country;
  status?: ProjectStatus;
  description?: string;
}

export interface UpdateCheckResultRequest {
  status: CheckStatus;
  memo?: string;
}

export interface AddAccessRequest {
  userId: string;
  permission: 'view' | 'edit';
}

// ラベル定義
export const RISK_CATEGORY_LABELS: Record<RiskCategory, string> = {
  legal: '法的リスク',
  economic: '経済的リスク',
  financial: '財政的リスク',
  international: '国際的リスク',
  domestic_political: '国内政治的リスク',
  reputation: '対世論リスク',
  employee: '従業員リスク',
  labor_union: '労働組合リスク',
};

export const CHECK_STATUS_LABELS: Record<CheckStatus, string> = {
  unchecked: '未確認',
  completed: '完了',
  not_applicable: '該当なし',
  needs_review: '要確認',
};

export const MA_METHOD_LABELS: Record<MAMethod, string> = {
  stock_transfer: '株式譲渡',
  business_transfer: '事業譲渡',
  merger: '合併',
  company_split: '会社分割',
  stock_exchange: '株式交換・移転',
};

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  planning: '検討中',
  in_progress: '進行中',
  completed: '完了',
  cancelled: '中止',
};

export const COUNTRY_LABELS: Record<Country, string> = {
  japan: '日本',
  usa: '米国',
  uk: '英国',
  eu: 'EU',
  china: '中国',
};

// バックエンドレスポンス型（snake_case）
interface BackendProjectResponse {
  id: string;
  name: string;
  ma_method: MAMethod;
  target_company: string;
  target_country: Country;
  status: ProjectStatus;
  description?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface BackendCheckResultResponse {
  id: string;
  project_id: string;
  check_item_id: string;
  status: CheckStatus;
  memo?: string;
  updated_by: string;
  updated_at: string;
}

interface BackendProjectAccessResponse {
  id: string;
  project_id: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  permission: 'view' | 'edit';
  granted_at: string;
}

interface BackendUserResponse {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'reviewer' | 'user';
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

interface BackendProjectDetailResponse extends BackendProjectResponse {
  // snake_case（旧バックエンド）
  check_results?: BackendCheckResultResponse[];
  access_permissions?: BackendProjectAccessResponse[];
  // camelCase（新バックエンド）
  checkResults?: BackendCheckResultResponse[];
  accessPermissions?: BackendProjectAccessResponse[];
}

interface BackendProjectWithCreatorResponse extends BackendProjectResponse {
  creator?: BackendUserResponse;
}

// 変換関数: バックエンドのsnake_case -> フロントエンドのcamelCase
function convertProject(p: BackendProjectResponse): Project {
  return {
    id: p.id,
    name: p.name,
    maMethod: p.ma_method,
    targetCompany: p.target_company,
    targetCountry: p.target_country,
    status: p.status,
    description: p.description,
    createdBy: p.created_by,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
  };
}

function convertCheckResult(r: BackendCheckResultResponse): CheckResult {
  return {
    id: r.id,
    projectId: r.project_id,
    checkItemId: r.check_item_id,
    status: r.status,
    memo: r.memo,
    updatedBy: r.updated_by,
    updatedAt: r.updated_at,
  };
}

function convertProjectAccess(a: BackendProjectAccessResponse): ProjectAccess {
  return {
    id: a.id,
    projectId: a.project_id,
    userId: a.user_id,
    userName: a.user_name,
    userEmail: a.user_email,
    permission: a.permission,
    grantedAt: a.granted_at,
  };
}

function convertUser(u: BackendUserResponse): User {
  return {
    id: u.id,
    email: u.email,
    name: u.name,
    role: u.role,
    status: u.status,
    createdAt: u.created_at,
    updatedAt: u.updated_at,
  };
}

function convertProjectDetail(p: BackendProjectDetailResponse): ProjectDetail {
  // バックエンドがcheck_resultsを返さない場合は空配列をデフォルトとする
  const checkResults = p.check_results || p.checkResults || [];
  const accessPermissions = p.access_permissions || p.accessPermissions;

  return {
    ...convertProject(p),
    checkResults: Array.isArray(checkResults) ? checkResults.map(convertCheckResult) : [],
    accessPermissions: Array.isArray(accessPermissions) ? accessPermissions.map(convertProjectAccess) : undefined,
  };
}

function convertProjectWithCreator(p: BackendProjectWithCreatorResponse): ProjectWithCreator {
  return {
    ...convertProject(p),
    creator: p.creator ? convertUser(p.creator) : undefined,
  };
}

export const projectService = {
  // GET /api/projects - 案件一覧取得
  async getProjects(filters?: ProjectFilters): Promise<Project[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.keyword) params.append('keyword', filters.keyword);
      if (filters?.maMethod) params.append('ma_method', filters.maMethod);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.targetCountry) params.append('target_country', filters.targetCountry);

      const query = params.toString();
      const url = query ? `/api/projects?${query}` : '/api/projects';
      const response = await apiClient.get<{ data: BackendProjectResponse[] }>(url);
      const data = response.data?.data || response.data || [];
      if (!Array.isArray(data)) return [];
      return data.map(convertProject);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // POST /api/projects - 案件作成
  async createProject(data: CreateProjectRequest): Promise<Project> {
    try {
      const response = await apiClient.post<BackendProjectResponse>('/api/projects', {
        name: data.name,
        ma_method: data.maMethod,
        target_company: data.targetCompany,
        target_country: data.targetCountry,
        description: data.description,
      });
      return convertProject(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // GET /api/projects/:id - 案件詳細取得
  async getProject(id: string): Promise<ProjectDetail> {
    try {
      const response = await apiClient.get<BackendProjectDetailResponse>(`/api/projects/${id}`);
      return convertProjectDetail(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // PUT /api/projects/:id - 案件更新
  async updateProject(id: string, data: UpdateProjectRequest): Promise<Project> {
    try {
      const requestData: Record<string, unknown> = {};
      if (data.name !== undefined) requestData.name = data.name;
      if (data.maMethod !== undefined) requestData.ma_method = data.maMethod;
      if (data.targetCompany !== undefined) requestData.target_company = data.targetCompany;
      if (data.targetCountry !== undefined) requestData.target_country = data.targetCountry;
      if (data.status !== undefined) requestData.status = data.status;
      if (data.description !== undefined) requestData.description = data.description;

      const response = await apiClient.put<BackendProjectResponse>(
        `/api/projects/${id}`,
        requestData
      );
      return convertProject(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // DELETE /api/projects/:id - 案件削除
  async deleteProject(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/projects/${id}`);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // GET /api/projects/:id/checks - チェック結果取得
  async getCheckResults(projectId: string): Promise<CheckResult[]> {
    try {
      const response = await apiClient.get<{ data: BackendCheckResultResponse[] }>(
        `/api/projects/${projectId}/checks`
      );
      const data = response.data?.data || response.data || [];
      if (!Array.isArray(data)) return [];
      return data.map(convertCheckResult);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // PUT /api/projects/:id/checks - チェック結果更新（一括更新）
  async updateCheckResults(
    projectId: string,
    results: Array<{ checkItemId: string; status: CheckStatus; memo?: string }>
  ): Promise<CheckResult[]> {
    try {
      const requestData = results.map((r) => ({
        check_item_id: r.checkItemId,
        status: r.status,
        memo: r.memo,
      }));

      const response = await apiClient.put<{ data: BackendCheckResultResponse[] }>(
        `/api/projects/${projectId}/checks`,
        requestData
      );
      const data = response.data?.data || response.data || [];
      if (!Array.isArray(data)) return [];
      return data.map(convertCheckResult);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // PUT /api/projects/:id/checks/:checkItemId - 個別チェック結果更新
  async updateCheckResult(
    projectId: string,
    checkItemId: string,
    data: UpdateCheckResultRequest
  ): Promise<CheckResult> {
    try {
      const response = await apiClient.put<BackendCheckResultResponse>(
        `/api/projects/${projectId}/checks/${checkItemId}`,
        { status: data.status, memo: data.memo }
      );
      return convertCheckResult(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // GET /api/projects/:id/access - アクセス権取得
  async getAccessPermissions(projectId: string): Promise<ProjectAccess[]> {
    try {
      const response = await apiClient.get<{ data: BackendProjectAccessResponse[] }>(
        `/api/projects/${projectId}/access`
      );
      const data = response.data?.data || response.data || [];
      if (!Array.isArray(data)) return [];
      return data.map(convertProjectAccess);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // POST /api/projects/:id/access - アクセス権追加
  async addAccessPermission(projectId: string, data: AddAccessRequest): Promise<ProjectAccess> {
    try {
      const response = await apiClient.post<BackendProjectAccessResponse>(
        `/api/projects/${projectId}/access`,
        {
          user_id: data.userId,
          permission: data.permission,
        }
      );
      return convertProjectAccess(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // DELETE /api/projects/:id/access/:accessId - アクセス権削除
  async removeAccessPermission(projectId: string, accessId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/projects/${projectId}/access/${accessId}`);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // ===========================================
  // 管理者用メソッド
  // ===========================================

  // GET /api/admin/projects - 全案件一覧取得
  async getAllProjects(filters?: AdminProjectFilters): Promise<ProjectWithCreator[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.keyword) params.append('keyword', filters.keyword);
      if (filters?.maMethod) params.append('ma_method', filters.maMethod);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.targetCountry) params.append('target_country', filters.targetCountry);
      if (filters?.creatorId) params.append('creator_id', filters.creatorId);

      const query = params.toString();
      const url = query ? `/api/admin/projects?${query}` : '/api/admin/projects';
      const response = await apiClient.get<{ data: BackendProjectWithCreatorResponse[] }>(url);
      const data = response.data?.data || response.data || [];
      if (!Array.isArray(data)) return [];
      return data.map(convertProjectWithCreator);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // GET /api/admin/projects/stats - 案件統計取得
  async getProjectStats(): Promise<ProjectStats> {
    try {
      const response = await apiClient.get<{ data: ProjectStats }>('/api/admin/projects/stats');
      return response.data.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // DELETE /api/admin/projects/:id - 案件削除（管理者用）
  async adminDeleteProject(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/admin/projects/${id}`);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },
};

export default projectService;
